// src/constants/categories.ts
import { GiftGroup } from "../types";
import { RecipientType } from "../types/card.types";
import { ArchetypeId } from "./archetypes";

// Extend the subcategory type to include archetype
// (If you have a separate type file, update it accordingly)
export interface GiftSubcategory {
  id: string;
  name: string;
  label: string;
  title: string;
  description: string;
  image: string;
  price: string;
  tags: string[];
  archetype: ArchetypeId; // <-- added
}

export const GIFT_GROUPS: GiftGroup[] = [
  {
    id: "romantic",
    name: "Romantic",
    title: "For The One You Love",
    label: "Your Romantic Interest",
    description:
      "Craft a deeply personal bouquet and card experience designed to celebrate romance, passion, and unforgettable emotional moments.",
    image:
      "https://images.pexels.com/photos/1024960/pexels-photo-1024960.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
    price: "KES 1,000",
    tags: ["valentine", "anniversary", "romance"],
    trendingScore: 100,
    subcategories: [
      {
        id: "romantic_partner",
        name: "Girlfriend/Boyfriend",
        label: "Your Partner",
        title: "A Love Experience They'll Never Forget",
        description:
          "For the one who stands by you through everything. Celebrate your unique bond.",
        image:
          "https://images.pexels.com/photos/2253870/pexels-photo-2253870.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["valentine", "anniversary", "romance"],
        archetype: "heartbeat",
      },
      {
        id: "romantic_crush",
        name: "Crush",
        label: "Your Crush",
        title: "Say It Without Saying Too Much",
        description:
          "A playful and subtle digital love experience perfect for expressing admiration.",
        image:
          "https://images.pexels.com/photos/3769021/pexels-photo-3769021.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["flirting", "new love", "secret feelings"],
        archetype: "heartbeat",
      },
      {
        id: "romantic_spouse",
        name: "Spouse",
        label: "Your Spouse",
        title: "Celebrate The Love You Built Together",
        description:
          "Celebrate commitment, shared memories, and lifelong partnership.",
        image:
          "https://images.pexels.com/photos/3692851/pexels-photo-3692851.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["marriage", "anniversary", "commitment"],
        archetype: "heartbeat",
      },
    ],
  },

  {
    id: "friendship",
    name: "Friends",
    title: "For Your Chosen Family",
    label: "Your Friend",
    description: "Celebrate loyalty, laughter, and lifelong friendship.",
    image:
      "https://images.pexels.com/photos/7752794/pexels-photo-7752794.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
    price: "KES 1,000",
    tags: ["friendship", "fun", "appreciation"],
    trendingScore: 91,
    subcategories: [
      {
        id: "friend_best",
        name: "Best Friend",
        label: "Your Best Friend",
        title: "Celebrate The One Who Knows Everything About You",
        description:
          "A fun and emotional experience celebrating loyalty and laughter.",
        image:
          "https://images.pexels.com/photos/1029635/pexels-photo-1029635.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["friendship", "fun"],
        archetype: "heartbeat",
      },
      {
        id: "friend_close",
        name: "Close friend",
        label: "Your Best Friend",
        title: "Celebrate The One Who Knows Everything About You",
        description:
          "A fun and emotional experience celebrating loyalty and laughter.",
        image:
          "https://images.pexels.com/photos/1029635/pexels-photo-1029635.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["friendship", "fun"],
        archetype: "heartbeat",
      },
    ],
  },

  {
    id: "self",
    name: "Self",
    title: "Celebrate Yourself",
    label: "Yourself",
    description:
      "Design a personal bouquet celebrating growth, confidence, and emotional well-being.",
    image:
      "https://images.pexels.com/photos/3768916/pexels-photo-3768916.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
    price: "KES 1,000",
    tags: ["wellness", "selfcare", "growth"],
    trendingScore: 79,
    subcategories: [
      {
        id: "self_love",
        name: "Self Love",
        label: "Yourself",
        title: "Celebrate Yourself Without Apology",
        description:
          "Design a personal bouquet celebrating growth, confidence, and emotional well-being.",
        image:
          "https://images.pexels.com/photos/3962294/pexels-photo-3962294.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["wellness", "selfcare"],
        archetype: "heartbeat",
      },
      {
        id: "self_celebration",
        name: "Personal Celebration",
        label: "Your Achievements",
        title: "Celebrate Your Wins, Big or Small",
        description:
          "Mark achievements and milestones with a personal celebration.",
        image:
          "https://images.pexels.com/photos/7249946/pexels-photo-7249946.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["success", "achievement"],
        archetype: "moments",
      },
    ],
  },

  {
    id: "occasion",
    name: "Occasions",
    title: "Celebrate Special Moments",
    label: "The Occasion",
    description:
      "Create a fun and interactive experience filled with joy and emotional surprises.",
    image:
      "https://images.pexels.com/photos/2977565/pexels-photo-2977565.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
    price: "KES 1,000",
    tags: ["birthday", "anniversary", "celebration"],
    trendingScore: 95,
    subcategories: [
      {
        id: "occasion_valentine",
        name: "Valentine's",
        label: "Valentine Celebration",
        title: "The Ultimate Digital Valentine Experience",
        description:
          "Celebrate Valentine's Day with a visually stunning emotional experience.",
        image:
          "https://images.pexels.com/photos/761977/pexels-photo-761977.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["valentine", "romance"],
        archetype: "heartbeat",
      },
      {
        id: "occasion_birthday",
        name: "Birthday",
        label: "Birthday Celebration",
        title: "Make Their Birthday Unforgettable",
        description: "Create a fun and interactive birthday experience.",
        image:
          "https://images.pexels.com/photos/1702373/pexels-photo-1702373.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["birthday", "celebration"],
        archetype: "moments",
      },
      {
        id: "occasion_anniversary",
        name: "Anniversary",
        label: "Anniversary Celebration",
        title: "Celebrate Love, Time, and Memories",
        description: "Honor shared memories and relationship milestones.",
        image:
          "https://images.pexels.com/photos/2253870/pexels-photo-2253870.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["anniversary", "romance"],
        archetype: "heartbeat",
      },
      {
        id: "occasion_graduation",
        name: "Graduation",
        label: "Graduation Achievement",
        title: "Celebrate Academic Success and New Beginnings",
        description:
          "Recognize educational milestones and celebrate future opportunities.",
        image:
          "https://images.pexels.com/photos/267885/pexels-photo-267885.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["graduation", "success"],
        archetype: "moments",
      },
      {
        id: "occasion_promotion",
        name: "Promotion",
        label: "Career Promotion",
        title: "Celebrate Career Growth and Achievement",
        description: "Honor professional milestones and celebrate hard work.",
        image:
          "https://images.pexels.com/photos/5668776/pexels-photo-5668776.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["career", "achievement"],
        archetype: "moments",
      },
      {
        id: "occasion_wedding",
        name: "Wedding",
        label: "Wedding Celebration",
        title: "Celebrate The Beginning of Forever",
        description: "Send emotional congratulations to newlyweds.",
        image:
          "https://images.pexels.com/photos/169198/pexels-photo-169198.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["wedding", "celebration"],
        archetype: "moments",
      },
    ],
  },

  {
    id: "support",
    name: "Support",
    title: "Show You Care",
    label: "Someone You Support",
    description:
      "Send emotional encouragement and heartfelt blessings through a comforting digital gift.",
    image:
      "https://images.pexels.com/photos/6646900/pexels-photo-6646900.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
    price: "KES 1,000",
    tags: ["encouragement", "support", "blessings"],
    trendingScore: 97,
    subcategories: [
      {
        id: "support_blessing",
        name: "Blessings",
        label: "Someone You Want To Bless",
        title: "Send Encouragement, Love, and Positive Energy",
        description: "Send emotional encouragement and heartfelt blessings.",
        image:
          "https://images.pexels.com/photos/672358/pexels-photo-672358.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["encouragement", "support"],
        archetype: "safehaven",
      },
      {
        id: "support_money_bouquet",
        name: "Money Bouquet",
        label: "Someone You Want To Support Financially",
        title: "Symbolically Support Someone's Needs and Dreams",
        description: "Create a symbolic money bouquet experience.",
        image:
          "https://images.pexels.com/photos/3943729/pexels-photo-3943729.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["money", "support"],
        archetype: "safehaven",
      },
      {
        id: "support_soft_life",
        name: "Soft Life Support",
        label: "Someone Living The Soft Life",
        title: "Sponsor Joy, Relaxation, and Happiness",
        description:
          "Celebrate lifestyle enjoyment with a playful gifting experience.",
        image:
          "https://images.pexels.com/photos/3771082/pexels-photo-3771082.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["fun", "luxury"],
        archetype: "safehaven",
      },
      {
        id: "support_dream",
        name: "Dream Support",
        label: "Someone Chasing Their Dreams",
        title: "Encourage Their Ambitions and Vision",
        description: "Send emotional and motivational support for dreamers.",
        image:
          "https://images.pexels.com/photos/355948/pexels-photo-355948.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["motivation", "dreams"],
        archetype: "safehaven",
      },
    ],
  },

  {
    id: "viral",
    name: "Viral",
    title: "Make It Trend",
    label: "Your Viral Moment",
    description:
      "A viral-friendly emotional gesture for spreading positivity and appreciation.",
    image:
      "https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
    price: "KES 1,000",
    tags: ["viral", "trending", "social"],
    trendingScore: 99,
    subcategories: [
      {
        id: "viral_thinking_of_you",
        name: "Thinking Of You",
        label: "Someone On Your Mind",
        title: "A Small Gesture That Means Everything",
        description: "Send a spontaneous digital love gesture.",
        image:
          "https://images.pexels.com/photos/1073097/pexels-photo-1073097.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["spontaneous", "cute"],
        archetype: "heartbeat",
      },
      {
        id: "viral_random_love",
        name: "Random Love",
        label: "Someone You Appreciate",
        title: "Spread Unexpected Love and Kindness",
        description:
          "A viral-friendly emotional gesture for spreading positivity.",
        image:
          "https://images.pexels.com/photos/7262514/pexels-photo-7262514.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["kindness", "viral"],
        archetype: "heartbeat",
      },
    ],
  },

  {
    id: "family",
    name: "Family",
    title: "For The Ones Who Raised You",
    label: "Your Family Member",
    description:
      "A heartfelt digital bouquet designed to show gratitude, warmth, and unconditional love.",
    image:
      "https://images.pexels.com/photos/3771108/pexels-photo-3771108.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
    price: "KES 1,000",
    tags: ["family", "gratitude", "appreciation"],
    trendingScore: 90,
    subcategories: [
      {
        id: "family_mother",
        name: "Mother",
        label: "Your Mother",
        title: "Celebrate The Woman Who Raised You",
        description:
          "A heartfelt digital bouquet designed to show gratitude, warmth, and unconditional love.",
        image:
          "https://images.pexels.com/photos/733475/pexels-photo-733475.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["mothers day", "family", "gratitude"],
        archetype: "heartbeat",
      },
      {
        id: "family_father",
        name: "Father",
        label: "Your Father",
        title: "Celebrate Strength, Guidance, and Legacy",
        description:
          "Send appreciation for mentorship, protection, and life lessons.",
        image:
          "https://images.pexels.com/photos/4261781/pexels-photo-4261781.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["fathers day", "family", "respect"],
        archetype: "heartbeat",
      },
      {
        id: "family_children",
        name: "Child",
        label: "Your Parents",
        title: "Celebrate The Love That Built Your World",
        description:
          "A beautiful combined appreciation experience for both parents.",
        image:
          "https://images.pexels.com/photos/6238131/pexels-photo-6238131.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["family", "appreciation"],
        archetype: "heartbeat",
      },
      {
        id: "family_sibling",
        name: "Sibling",
        label: "Your Sibling",
        title: "Celebrate The Bond That Grew With You",
        description:
          "A playful or emotional experience celebrating sibling loyalty.",
        image:
          "https://images.pexels.com/photos/8129437/pexels-photo-8129437.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["family", "fun", "memories"],
        archetype: "heartbeat",
      },
      {
        id: "family_extended",
        name: "Extended Family",
        label: "Your Family Member",
        title: "Celebrate Family Beyond The Immediate Circle",
        description:
          "Show appreciation to relatives who played important roles in your life.",
        image:
          "https://images.pexels.com/photos/7524717/pexels-photo-7524717.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        price: "KES 1,000",
        tags: ["gratitude", "family"],
        archetype: "heartbeat",
      },
    ],
  },
];

// For backward compatibility
export const GIFT_CATEGORIES = GIFT_GROUPS.flatMap(
  (group) => group.subcategories,
);

export const RECIPIENT_TYPES: Array<{
  value: RecipientType;
  label: string;
  icon: string;
  description: string;
}> = [
  {
    value: "partner",
    label: "Partner",
    icon: "💑",
    description: "Your significant other (girlfriend or boyfriend",
  },
  {
    value: "spouse",
    label: "Spouse",
    icon: "💍",
    description: "Husband or wife",
  },
  {
    value: "crush",
    label: "Crush",
    icon: "💘",
    description: "Someone you admire",
  },
  { value: "mother", label: "Mother", icon: "👩‍👧", description: "Mom" },
  { value: "father", label: "Father", icon: "👨‍👦", description: "Dad" },
  {
    value: "child",
    label: "Child",
    icon: "👨‍👦",
    description: "Son or Daughter",
  },
  {
    value: "sibling",
    label: "Sibling",
    icon: "👫",
    description: "Brother or sister",
  },
  {
    value: "best_friend",
    label: "Best Friend",
    icon: "👯",
    description: "Bestie",
  },
  {
    value: "close_friend",
    label: "Close Friend",
    icon: "👯",
    description: "Friend",
  },
  {
    value: "self_love",
    label: "Self",
    icon: "",
    description: "Treat yourself",
  },
];

export const APP_CONFIG = {
  currency: "KES",
  siteName: "Lrnsy",
  siteUrl:
    process.env.NODE_ENV === "production"
      ? "https://lrnsy.com"
      : "http://localhost:3000",
  maxBouquetItems: 50,
  cardExpiryDays: 30,
};
